import { useEffect, useMemo, type ReactNode } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import { fetchPublishedForm, fetchPublishedFormBySlug } from "@/api/forms";
import { fetchPreviewForm } from "@/api/preview-forms";
import { ThemeProvider } from "@/components/ThemeProvider";
import { PreviewModeProvider } from "@/contexts/PreviewModeContext";
import { LoadingState } from "../components/form-view/components/LoadingState";
import { ErrorState } from "../components/form-view/components/ErrorState";
import { IntroScreen } from "../components/form-view/components/IntroScreen";
import { CompletionScreen } from "../components/form-view/components/CompletionScreen";
import { RuntimeScreen } from "../components/form-view/components/RuntimeScreen";
import { ConversationalScreen } from "../components/form-view/components/ConversationalScreen";
import { useFormViewModel } from "../components/form-view/useFormViewModel";

/** Check if a string looks like a MongoDB ObjectId (24 hex chars) */
const isObjectId = (str: string): boolean => /^[a-f\d]{24}$/i.test(str);

type FormViewMode = "published" | "preview";

interface FormViewProps {
  mode?: FormViewMode;
}

export default function FormView({ mode = "published" }: FormViewProps) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  // Params can be either slugs or IDs - we detect which one at runtime
  const { projectId: projectParam, formId: formParam, version } = useParams();
  const isPreview = mode === "preview";

  // Determine if we're using slug-based or ID-based routing
  const isSlugBased = useMemo(() => {
    if (isPreview) {
      return false;
    }
    if (!projectParam || !formParam) return false;
    // If either param doesn't look like an ObjectId, treat as slugs
    return !isObjectId(projectParam) || !isObjectId(formParam);
  }, [formParam, isPreview, projectParam]);

  useEffect(() => {
    if (isPreview) {
      return;
    }
    if (!projectParam || !formParam || !version) {
      return;
    }

    navigate(`/p/${projectParam}/f/${formParam}`, { replace: true });
  }, [formParam, isPreview, navigate, projectParam, version]);

  const {
    data: form,
    isLoading,
    error,
  } = useQuery({
    queryKey: isPreview
      ? ["preview-form", token]
      : ["public-form", projectParam, formParam, isSlugBased],
    queryFn: () => {
      if (isPreview) {
        if (!token) {
          throw new Error("Preview token is required");
        }
        return fetchPreviewForm(token);
      }

      return isSlugBased
        ? fetchPublishedFormBySlug(projectParam!, formParam!)
        : fetchPublishedForm(projectParam!, formParam!);
    },
    enabled: isPreview ? Boolean(token) : Boolean(projectParam && formParam),
    staleTime: isPreview ? 0 : 1000 * 60 * 5,
    retry: isPreview ? false : undefined,
  });

  if (isPreview && !token) {
    return <ErrorState error={new Error("Preview token is required")} />;
  }

  // Use the actual IDs from the form response for downstream operations
  const projectId = form?.projectId ?? projectParam;
  const formId = form?.formId ?? formParam;

  const viewModel = useFormViewModel({ form, projectId, formId, isPreview });

  const introductionCopy = form?.ui.copy?.introduction?.trim();
  const thankYouCopy = form?.ui.copy?.thankYou?.trim();
  const estimatedMinutes = form?.ui.copy?.estimatedMinutes ?? undefined;

  const theme = form?.ui?.theme;
  const logoUrl = theme?.logoUrl;
  const formName = undefined;
  const layoutAlignment = form?.ui?.layout?.alignment;
  const alignment: "left" | "center" | "right" =
    layoutAlignment === "left" || layoutAlignment === "right"
      ? layoutAlignment
      : "center";

  const withProviders = (node: ReactNode) => (
    <ThemeProvider theme={theme}>
      {isPreview ? (
        <PreviewModeProvider isPreview={true}>{node}</PreviewModeProvider>
      ) : (
        node
      )}
    </ThemeProvider>
  );

  if (isLoading) {
    return <LoadingState />;
  }

  if (error || !form) {
    return <ErrorState error={error} />;
  }

  if (!projectId || !formId) {
    return <ErrorState error={new Error("Form identifiers are missing")} />;
  }

  // Render conversational mode if layout variant is 'conversational'
  const isSharedMode = !isPreview && form.responseMode === "shared";
  const isConversational =
    !isSharedMode && !isPreview && form.ui?.layout?.variant === "conversational";
  if (isConversational && projectId && formId) {
    return (
      <ConversationalScreen
        projectId={projectId}
        formId={formId}
        version={form.version}
        formVersion={form}
      />
    );
  }

  if (viewModel.isRestoringDraft) {
    return <LoadingState />;
  }

  if (viewModel.isOnIntro) {
    return withProviders(
      <IntroScreen
        alignment={alignment}
        logoUrl={logoUrl}
        formName={formName}
        theme={theme}
        introductionCopy={introductionCopy}
        estimatedMinutes={estimatedMinutes}
        onStart={viewModel.onNext}
        startLabel={isSharedMode ? "Open form" : "Start"}
        hasDraft={viewModel.hasDraft}
        draftUpdatedAt={viewModel.draftUpdatedAt}
        onResumeDraft={viewModel.onResumeDraft}
        onDiscardDraft={viewModel.onDiscardDraft}
      />,
    );
  }

  if (!isSharedMode && viewModel.isComplete) {
    return withProviders(
      <CompletionScreen
        alignment={alignment}
        logoUrl={logoUrl}
        formName={formName}
        theme={theme}
        thankYouCopy={thankYouCopy}
        responseId={viewModel.responseId}
        isSubmitted={viewModel.isSubmitted}
        onReset={viewModel.onReset}
      />,
    );
  }

  return withProviders(
    <RuntimeScreen
      projectId={projectId}
      formId={formId}
      version={form.version}
      alignment={alignment}
      logoUrl={logoUrl}
      formName={formName}
      theme={theme}
      showProgress={viewModel.showProgress}
      progressMode={viewModel.progressMode}
      progressPercentage={viewModel.progressPercentage}
      currentPage={viewModel.hasPages ? viewModel.currentPageIndex : undefined}
      totalPages={viewModel.hasPages ? viewModel.totalPages : undefined}
      currentStep={viewModel.currentStep}
      totalQuestions={viewModel.totalQuestions}
      submissionError={viewModel.submissionError}
      validationErrors={viewModel.validationErrors}
      onDismissSubmissionError={viewModel.onDismissSubmissionError}
      currentQuestion={viewModel.currentQuestion}
      currentPageQuestions={viewModel.currentPageQuestions}
      questions={viewModel.questions}
      answers={viewModel.answers}
      sessionToken={viewModel.uploadSessionToken}
      ensureSessionToken={viewModel.ensureUploadSessionToken}
      onAnswerChange={viewModel.onAnswerChange}
      onAnswerBlur={viewModel.onAnswerBlur}
      getQuestionError={viewModel.getQuestionError}
      onBack={viewModel.onBack}
      onNext={viewModel.onNext}
      canProceed={viewModel.canProceed}
      isSubmitting={viewModel.isSubmitting}
      backDisabled={viewModel.backDisabled}
      isLastStep={viewModel.isLastStep}
      isSharedMode={isSharedMode}
    />,
  );
}
